import { takeLatest, put, call } from 'redux-saga/effects';
import { history, storage } from '@commons';
import {
  createPlanejamento,
  createPlanejamentoMulti,
  updatePlanejamento,
} from '../service';
import {
  CONFIG_SESSION,
  CREATE_PLANEJAMENTO,
  CREATE_PLANEJAMENTO_PENDING,
  CREATE_PLANEJAMENTO_SUCCESS,
  CREATE_PLANEJAMENTO_FAILURE,
  findConversao,
} from '@handler';

import {
  AlertSuccess,
  AlertError,
  AlertWarning,
} from '@components/common/AlertToast';
import {
  formatDateDDMMYYYY,
  formatJsonRemoveEmptyOrNull,
  isDateValidVerifyDateBetween,
} from '@components/common/format';
import { trackingEventCX, MODULE_ACTIONS_ENUM } from '@handler/tracking/customerX';

function* sagaCreatePlanejamento(action) {
  yield put({ type: CREATE_PLANEJAMENTO_PENDING });

  try {
    const safraAtiva = JSON.parse(storage.get(CONFIG_SESSION)).safraAtiva;
    const { dataInicial: dateFrom, dataFinal: dateTo } = safraAtiva;
    const dateCheck = action.data.data;

    if (!isDateValidVerifyDateBetween(dateFrom, dateTo, dateCheck)) {
      yield put({ type: CREATE_PLANEJAMENTO_FAILURE, registro: action.data });
      AlertWarning(`Data informada fora do período da safra ativa. Período deve ser entre ${formatDateDDMMYYYY(dateFrom)} até ${formatDateDDMMYYYY(dateTo)}!`);
    } else {
      if (action.data.replicarLancamento) {
        if (action.data.tipoLancamentoTalhao == 'RATEAR') {
          let procedimentosASalvar = [];

          // Calcular a área total de todos os talhões
          let areaTotal = 0;
          for (let i = 0; i < action.data.talhoesList.length; i++) {
            areaTotal += parseFloat(action.data.talhoesList[i].area || 0);
          }

          // Para cada talhão, criar um lançamento com valores proporcionais
          for (let i = 0; i < action.data.talhoesList.length; i++) {
            let talhao = action.data.talhoesList[i];
            let areaTalhao = parseFloat(talhao.area || 0);
            let proporcao = areaTotal > 0 ? areaTalhao / areaTotal : 0;
            
            let procedimentoTalhao = {
              ...action.data,
              'forJson': 'AtivPlanejamento',
              'sync': false,
              propriedade: {
                id: JSON.parse(storage.get(CONFIG_SESSION)).fazendaSelecionada.id
              }
            };
            
            procedimentoTalhao.talhao = { ...talhao };
            if (procedimentoTalhao.talhao.talhao !== undefined && procedimentoTalhao.talhao.talhao !== null) {
              procedimentoTalhao.talhao = procedimentoTalhao.talhao.talhao;
            }
            
            // TRATAMENTO DE MAQUINÁRIOS
            procedimentoTalhao.utilizacaoMaquinarios = [];
            procedimentoTalhao.custoTotalMaquinarios = 0;
            
            if (action.data.utilizacaoMaquinarios && action.data.utilizacaoMaquinarios.length > 0) {
              for (let j = 0; j < action.data.utilizacaoMaquinarios.length; j++) {
                let maqOriginal = action.data.utilizacaoMaquinarios[j];
                
                let maqRateado = JSON.parse(JSON.stringify(maqOriginal));
                
                let horasString = maqOriginal.horasQuilometrosTotais || "00:00";
                let [horas, minutos] = horasString.split(":").map(Number);
                let horasTotais = horas + (minutos / 60);
                
                // Calcular horas proporcionais
                let horasProporcionais = horasTotais * proporcao;
                let horasInteiras = Math.floor(horasProporcionais);
                let minutosProporcionais = Math.round((horasProporcionais - horasInteiras) * 60);
                
                maqRateado.horasQuilometrosTotais = 
                  `${horasInteiras.toString().padStart(2, '0')}:${minutosProporcionais.toString().padStart(2, '0')}`;
                
                // Recalcular o custo baseado nas horas proporcionais
                maqRateado.custoTotal = horasProporcionais * parseFloat(maqRateado.custoHoraQuilometro || 0);
                
                procedimentoTalhao.utilizacaoMaquinarios.push(maqRateado);
                procedimentoTalhao.custoTotalMaquinarios += maqRateado.custoTotal;
              }
            }
            
            // TRATAMENTO DE FUNCIONÁRIOS
            procedimentoTalhao.alocacaoFuncionarios = [];
            procedimentoTalhao.custoTotalFuncionarios = 0;
            
            if (action.data.alocacaoFuncionarios && action.data.alocacaoFuncionarios.length > 0) {
              for (let j = 0; j < action.data.alocacaoFuncionarios.length; j++) {
                let funcOriginal = action.data.alocacaoFuncionarios[j];
                
                let funcRateado = JSON.parse(JSON.stringify(funcOriginal));
                
                let horasString = funcOriginal.horasTrabalhadas || "00:00";
                let [horas, minutos] = horasString.split(":").map(Number);
                let horasTotais = horas + (minutos / 60);
                
                // Calcular horas proporcionais
                let horasProporcionais = horasTotais * proporcao;
                let horasInteiras = Math.floor(horasProporcionais);
                let minutosProporcionais = Math.round((horasProporcionais - horasInteiras) * 60);
                
                funcRateado.horasTrabalhadas = 
                  `${horasInteiras.toString().padStart(2, '0')}:${minutosProporcionais.toString().padStart(2, '0')}`;
                
                // Recalcular o custo baseado nas horas proporcionais
                funcRateado.custoTotal = horasProporcionais * parseFloat(funcRateado.custoHomemHora || 0);
                
                procedimentoTalhao.alocacaoFuncionarios.push(funcRateado);
                procedimentoTalhao.custoTotalFuncionarios += funcRateado.custoTotal;
              }
            }
            
            // TRATAMENTO DE INSUMOS (aplicacoes)
            procedimentoTalhao.aplicacoes = [];
            procedimentoTalhao.custoTotalProdutosInsumos = 0;

            if (action.data.aplicacoes && action.data.aplicacoes.length > 0) {
              for (let j = 0; j < action.data.aplicacoes.length; j++) {
                let aplicOriginal = action.data.aplicacoes[j];
                
                let aplicRateada = JSON.parse(JSON.stringify(aplicOriginal));
                
                let formaLanc = '';
                if (!aplicRateada.configLancAgricultura) {
                  formaLanc = safraAtiva.cultura.formaLancPlantio === 'POR_HA' ? 'POR_HA' : 'POR_APLI';
                } else {
                  formaLanc = aplicRateada.configLancAgriculturaTipo === 'POR_HA' ? 'POR_HA' : 'POR_APLI';
                }
                
                aplicRateada.qtdConversaoAplicacao = aplicOriginal.qtdConversaoAplicacao || 1;
                
                let dosagemOriginal = parseFloat(aplicOriginal.dosagemHa || 0);
                aplicRateada.dosagemHa = dosagemOriginal * proporcao;
                
                // Calcular custos com base na dosagem rateada
                let custoNota = parseFloat(aplicRateada.custoProduto || 0) / aplicRateada.qtdConversaoAplicacao;
                
                if (formaLanc === 'POR_HA') {
                  aplicRateada.custoTotalHa = aplicRateada.dosagemHa * custoNota;
                  aplicRateada.custoTotalTalhao = aplicRateada.custoTotalHa * areaTalhao;
                } else {
                  aplicRateada.custoTotalTalhao = aplicRateada.dosagemHa * custoNota;
                  aplicRateada.custoTotalHa = areaTalhao > 0 ? aplicRateada.custoTotalTalhao / areaTalhao : 0;
                }
                
                // Processar estoque
                aplicRateada.estoqueBaixado = false;
                if (aplicRateada.movimentaEstoque) {
                  aplicRateada.qtdConversaoEstoque = aplicOriginal.qtdConversaoEstoque || 1;
                  
                  if (formaLanc === 'POR_HA') {
                    aplicRateada.estoqueABaixar = aplicRateada.dosagemHa * areaTalhao * aplicRateada.qtdConversaoEstoque;
                  } else {
                    aplicRateada.estoqueABaixar = aplicRateada.dosagemHa * aplicRateada.qtdConversaoEstoque;
                  }
                  
                  if (aplicRateada.informarLocalEstoqueBaixa === false) {
                    aplicRateada.localEstoqueBaixa = null;
                  }
                } else {
                  aplicRateada.estoqueABaixar = 0;
                  aplicRateada.informarLocalEstoqueBaixa = false;
                  aplicRateada.localEstoqueBaixa = null;
                }
                
                aplicRateada.areaTalhao = areaTalhao;
                
                procedimentoTalhao.aplicacoes.push(aplicRateada);
                procedimentoTalhao.custoTotalProdutosInsumos += aplicRateada.custoTotalTalhao;
              }
            }
            
            procedimentosASalvar.push(procedimentoTalhao);
          }

          for (let i = 0; i < procedimentosASalvar.length; i++) {
            delete procedimentosASalvar[i].replicarLancamento;
            delete procedimentosASalvar[i].talhoesList;
            delete procedimentosASalvar[i].tipoLancamentoTalhao;

            // Calcular os custos totais
            procedimentosASalvar[i].custoTotalTalhao = (
              procedimentosASalvar[i].custoTotalProdutosInsumos +
              procedimentosASalvar[i].custoTotalMaquinarios +
              procedimentosASalvar[i].custoTotalFuncionarios
            );

            let areaTalhao = parseFloat(procedimentosASalvar[i].talhao?.area || 0);
            procedimentosASalvar[i].custoTotalHa = areaTalhao > 0 ?
              procedimentosASalvar[i].custoTotalTalhao / areaTalhao : 0;

            // Adiciona os IDs dos controles de praga vinculados
            procedimentosASalvar[i].vinculoControlePragaIds = action.data.controlesVinculados
              ? action.data.controlesVinculados.map(c => c.id)
              : [];

            // Remove campos que não devem ser enviados ao backend
            delete procedimentosASalvar[i].controlesVinculados;
            delete procedimentosASalvar[i].vincularControleFitossanitario;
            delete procedimentosASalvar[i].vinculoControlePraga;
          }

          yield call(createPlanejamentoMulti, procedimentosASalvar);
          trackingEventCX('action', MODULE_ACTIONS_ENUM.PLANEJAMENTO_SAFRA_CRIAR);

          yield put({ type: CREATE_PLANEJAMENTO_SUCCESS });
          history.push('/agricultura/planejamento');
          AlertSuccess('Registro salvo com sucesso!');
        } else {
          let procedimentosASalvar = []

          for (let index = 0; index < action.data.talhoesList.length; index++) {
            let procedimentoTalhao = {
              ...action.data,
              'forJson': 'AtivPlanejamento',
              'sync': false,
              propriedade: {
                id: JSON.parse(storage.get(CONFIG_SESSION)).fazendaSelecionada.id
              }
            };
            procedimentoTalhao.talhao = { ...action.data.talhoesList[index] }

            if (procedimentoTalhao.talhao.talhao !== undefined && procedimentoTalhao.talhao.talhao !== null) {
              procedimentoTalhao.talhao = procedimentoTalhao.talhao.talhao;
            }

            procedimentoTalhao.aplicacoes = [...action.data.aplicacoes]

            let custoTotalProdutosInsumos = 0;
            let areaTalhao = action.data.talhoesList[index].area;

            for (let indexAplic = 0; indexAplic < procedimentoTalhao.aplicacoes.length; indexAplic++) {
              let { custoTotalHa, custoTotalTalhao, estoqueABaixar } = calcularAplicacao(procedimentoTalhao.aplicacoes[indexAplic], areaTalhao, safraAtiva);
              Object.assign(procedimentoTalhao.aplicacoes[indexAplic], { areaTalhao, custoTotalHa, custoTotalTalhao, estoqueABaixar });

              procedimentoTalhao.aplicacoes[indexAplic] = {
                ...procedimentoTalhao.aplicacoes[indexAplic],
                areaTalhao: areaTalhao,
                custoTotalHa: custoTotalHa,
                custoTotalTalhao: custoTotalTalhao,
              }

              custoTotalProdutosInsumos += custoTotalTalhao;
            }
            procedimentoTalhao.custoTotalProdutosInsumos = custoTotalProdutosInsumos;

            procedimentosASalvar.push(procedimentoTalhao)
          }

          for (let index = 0; index < procedimentosASalvar.length; index++) {
            delete procedimentosASalvar[index].replicarLancamento;
            delete procedimentosASalvar[index].talhoesList;

            procedimentosASalvar[index].custoTotalTalhao = (procedimentosASalvar[index].custoTotalProdutosInsumos + procedimentosASalvar[index].custoTotalMaquinarios + procedimentosASalvar[index].custoTotalFuncionarios);
            procedimentosASalvar[index].custoTotalHa = (procedimentosASalvar[index].custoTotalTalhao / (procedimentosASalvar[index].talhao?.area ?? 0));

            // Adiciona os IDs dos controles de praga vinculados
            procedimentosASalvar[index].vinculoControlePragaIds = action.data.controlesVinculados
              ? action.data.controlesVinculados.map(c => c.id)
              : [];

            // Remove campos que não devem ser enviados ao backend
            delete procedimentosASalvar[index].controlesVinculados;
            delete procedimentosASalvar[index].vincularControleFitossanitario;
            delete procedimentosASalvar[index].vinculoControlePraga;
          }

          yield call(createPlanejamentoMulti, procedimentosASalvar);
          trackingEventCX('action', MODULE_ACTIONS_ENUM.PLANEJAMENTO_SAFRA_CRIAR);

          yield put({ type: CREATE_PLANEJAMENTO_SUCCESS });
          history.push('/agricultura/planejamento');
          AlertSuccess('Registro salvo com sucesso!');
        }
      } else {
        // Cálculo em uma cópia limpa de action.data
        const singlePlanejamento = {
          ...action.data,
          talhao: action.data.talhao.talhao || action.data.talhao, 
        };

        let custoTotalProdutosInsumos = 0;

        for (let aplicacao of singlePlanejamento.aplicacoes) {
          let areaTalhao = aplicacao.areaTalhao;
          
          let { custoTotalHa, custoTotalTalhao, estoqueABaixar } = calcularAplicacao(aplicacao, areaTalhao, safraAtiva);
          custoTotalProdutosInsumos += custoTotalTalhao;

          Object.assign(aplicacao, { areaTalhao, custoTotalHa, custoTotalTalhao, estoqueABaixar });
        }

        // Atualiza os totais sem alterar a estrutura original de action.data
        singlePlanejamento.custoTotalProdutosInsumos = custoTotalProdutosInsumos;
        singlePlanejamento.custoTotalTalhao = custoTotalProdutosInsumos + singlePlanejamento.custoTotalMaquinarios + singlePlanejamento.custoTotalFuncionarios;
        singlePlanejamento.custoTotalHa = singlePlanejamento.talhao?.area ? singlePlanejamento.custoTotalTalhao / singlePlanejamento.talhao.area : 0;

        // Extrai os IDs dos controles de praga vinculados
        const vinculoControlePragaIds = singlePlanejamento.controlesVinculados
          ? singlePlanejamento.controlesVinculados.map(c => c.id)
          : [];

        // Remove campos que não devem ser enviados ao backend
        delete singlePlanejamento.controlesVinculados;
        delete singlePlanejamento.vincularControleFitossanitario;
        delete singlePlanejamento.vinculoControlePraga;

        if (singlePlanejamento.id === undefined || singlePlanejamento.id === null) {
          yield call(createPlanejamento, formatJsonRemoveEmptyOrNull({
            ...singlePlanejamento,
            'forJson': 'AtivPlanejamento',
            'sync': false,
            propriedade: { id: JSON.parse(storage.get(CONFIG_SESSION)).fazendaSelecionada.id },
            vinculoControlePragaIds,
          }));
          trackingEventCX('action', MODULE_ACTIONS_ENUM.PLANEJAMENTO_SAFRA_CRIAR);
        } else {
          yield call(updatePlanejamento, {
            ...singlePlanejamento,
            vinculoControlePragaIds,
          });
          trackingEventCX('action', MODULE_ACTIONS_ENUM.PLANEJAMENTO_SAFRA_EDITAR);
        }

        yield put({ type: CREATE_PLANEJAMENTO_SUCCESS, registro: singlePlanejamento });
        history.push('/agricultura/planejamento');
        AlertSuccess('Registro salvo com sucesso!');
      }
    }
  } catch (error) {
    yield put({ type: CREATE_PLANEJAMENTO_FAILURE, registro: action.data });
    AlertError('Falha ao salvar o registro! Tente novamente...');
  }
}

function calcularAplicacao(aplicacao, areaTalhao, safraAtiva) {
  let formaLanc = aplicacao.configLancAgricultura ? aplicacao.configLancAgriculturaTipo : safraAtiva.cultura.formaLancPlantio;
  let custoTotalHa = 0;
  let custoTotalTalhao = 0;
  let estoqueABaixar = 0;

  let custoNota = aplicacao.custoProduto / aplicacao.qtdConversaoAplicacao;
  if (formaLanc === 'POR_HA') {
    custoTotalHa = aplicacao.dosagemHa * custoNota;
    custoTotalTalhao = custoTotalHa * areaTalhao;
  } else {
    custoTotalTalhao = aplicacao.dosagemHa * custoNota;
    custoTotalHa = custoTotalTalhao / areaTalhao;
  }

  if (aplicacao.movimentaEstoque) {
    estoqueABaixar = formaLanc === 'POR_HA' ? aplicacao.dosagemHa * areaTalhao * aplicacao.qtdConversaoEstoque : aplicacao.dosagemHa * aplicacao.qtdConversaoEstoque;
  }

  return { custoTotalHa, custoTotalTalhao, estoqueABaixar };
}

export default function* watchCreatePlanejamento() {
  yield takeLatest(CREATE_PLANEJAMENTO, sagaCreatePlanejamento);
}
